import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";
import { useConfig } from "../../Context/ConfigContext";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    permit_type: string;
    permit_authority: string;
    permit_number: string;
    issue_date: string;
    expiry_date: string;
    amount: string;
    document: FileList;
    remarks?: string;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function EditPermitRenewal() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [permitTypes, setPermitTypes] = useState<any[]>([]);
    const [permitAuthorities, setPermitAuthorities] = useState<any[]>([]);
    const [docName, setDocName] = useState("");
    const [docPreview, setDocPreview] = useState<string | null>(null);
    const [submissionBlocked, setSubmissionBlocked] = useState(false);
    const [pendingMessage, setPendingMessage] = useState("");
    const { hasPermission } = useAuth();
    const { config } = useConfig();

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<FormValues>();

    const selectedBranch = watch("branch_id");
    const selectedVehicle = watch("vehicle_id");
    const watchedIssueDate = watch("issue_date");
    const watchedExpiryDate = watch("expiry_date");

    // Pending check
    useEffect(() => {
        if (!selectedVehicle || !location.state?.id) { setSubmissionBlocked(false); setPendingMessage(""); return; }
        const checkPending = async () => {
            try {
                const response: any = await apiPost("permit_renewal/check_pending", { vehicle_id: selectedVehicle, exclude_id: location.state.id });
                if (response?.exists) { setPendingMessage(response.message); setSubmissionBlocked(true); }
                else { setSubmissionBlocked(false); setPendingMessage(""); }
            } catch { setSubmissionBlocked(false); setPendingMessage(""); }
        };
        checkPending();
    }, [selectedVehicle, location.state?.id]);

    useEffect(() => {
        if (!location.state || !location.state.id) {
            navigate(`${process.env.PUBLIC_URL}/permit_renewal`);
            return;
        }

        const fetchData = async () => {
            try {
                // Fetch Branches
                const branchesData: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchesData);

                // Fetch Permit Types
                const permitData: any = await apiPost('master-entries/list', { type: 'permit_type' });
                setPermitTypes(Array.isArray(permitData) ? permitData : []);

                // Fetch Permit Authorities
                const authorityData: any = await apiPost('master-entries/list', { type: 'permit_authority' });
                setPermitAuthorities(Array.isArray(authorityData) ? authorityData : []);

                // Fetch Permit Renewal Data
                const result: any = await apiPost("permit_renewal/viewbyid", { id: location.state.id });

                if (result) {
                    setValue("branch_id", result.branch_id);
                    // Fetch Vehicles for this branch
                    const vehiclesData: any = await apiPost("vehicles/list", { branch_id: result.branch_id });
                    if (Array.isArray(vehiclesData)) {
                        setVehicles(vehiclesData);
                    } else if (vehiclesData.data) {
                        setVehicles(vehiclesData.data);
                    }

                    setValue("vehicle_id", result.vehicle_id);
                    setValue("permit_type", result.permit_type);
                    setValue("permit_authority", result.permit_authority || "");
                    setValue("permit_number", result.permit_number || "");
                    setValue("issue_date", result.issue_date);
                    setValue("expiry_date", result.expiry_date);
                    setValue("amount", result.amount);
                    setValue("remarks", result.remarks);
                    setDocName(result.document);
                }
            } catch (error) {
                console.error(error);
                toast.error("Failed to fetch data");
            }
        };

        fetchData();
    }, [location.state, navigate, setValue]);

    // Handle Branch Change to update vehicles
    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) {
                return;
            }
            // Only fetch if it's a user interaction change, not initial load
            // However, for simplicity in edit, we load vehicles in the main useEffect. 
            // This Effect handles subsequent changes.
            if (vehicles.length === 0 && selectedBranch) {
                // Logic handled in main useEffect for initial load
            } else if (selectedBranch) {
                try {
                    const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                    if (Array.isArray(response)) {
                        const activeVehicles = response.filter((v: any) => v.status !== 'Sold');
                        setVehicles(activeVehicles);
                    } else if (response.data) {
                        const activeVehicles = Array.isArray(response.data) ? response.data.filter((v: any) => v.status !== 'Sold') : [];
                        setVehicles(activeVehicles);
                    } else {
                        setVehicles([]);
                    }
                } catch (error) {
                    console.error(error);
                    setVehicles([]);
                }
            }
        };

        // We only want to trigger this if branch changes AFTER initial load. 
        // But to keep it robust:
        if (selectedBranch && vehicles.length === 0) {
            // It might be initial load where vehicles haven't been set yet.
        } else if (selectedBranch) {
            // If user changes branch, clear vehicle?
            // Checking if the current vehicle belongs to new branch is complex without extra data.
            // Ideally clearing vehicle_id if user changes branch manually.
            // But we need to distinguish manual change vs initial set.
            // Leaving as is for now, main useEffect handles initial population.
        }

    }, [selectedBranch]);


    const onSubmit = async (data: FormValues) => {
        if (submissionBlocked) {
            toast.error(pendingMessage || "A pending permit renewal request already exists for this vehicle.");
            return;
        }

        setLoading(true);
        try {
            let newDocName = docName;
            if (data.document && data.document.length > 0) {
                const file = data.document[0];
                if (file.size > 2 * 1024 * 1024) {
                    toast.error("File size must be less than 2MB");
                    setLoading(false);
                    return;
                }
                newDocName = await uploadFile(file);
            }

            const payload: any = {
                id: location.state.id,
                ...data,
                issue_date: data.issue_date ? formatDate(new Date(data.issue_date)) : data.issue_date,
                expiry_date: data.expiry_date ? formatDate(new Date(data.expiry_date)) : data.expiry_date,
            };

            if (newDocName) {
                payload.document = newDocName;
            } else {
                delete payload.document;
                // If we uploaded new, we add it back
                if (newDocName !== docName) {
                    payload.document = newDocName;
                }
            }

            // Clean up FileList from payload
            if (payload.document instanceof FileList) {
                delete payload.document;
                if (newDocName) payload.document = newDocName;
            }

            // Auto-determine status: Completed if all mandatory fields filled
            // docName (state) = existing DB doc, newDocName = freshly uploaded
            const hasDocument = !!(newDocName || docName);
            payload.status = (
                data.branch_id &&
                data.vehicle_id &&
                data.permit_type &&
                data.issue_date &&
                data.expiry_date &&
                data.amount &&
                hasDocument
            ) ? 'Completed' : 'Pending';

            await apiPost("permit_renewal/update", payload);
            toast.success("Permit Renewal updated successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/permit_renewal`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error updating Permit renewal");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Edit Permit Renewal" parent="Renewal" title="Edit Permit Renewal" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <H5>Renewal Details</H5>
                            <hr />
                            <Row className="g-3">
                                <Col md={4}>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="branch_id"
                                        control={control}
                                        rules={{ required: "Branch is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.branch_id} disabled>
                                                    <option value="">Select Branch</option>
                                                    {branches.map((b: any) => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Vehicle <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="vehicle_id"
                                        control={control}
                                        rules={{ required: "Vehicle is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.vehicle_id} disabled>
                                                    <option value="">Select Vehicle</option>
                                                    {vehicles.map((v: any) => (
                                                        <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Permit Type <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="permit_type"
                                        control={control}
                                        rules={{ required: "Permit Type is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.permit_type}>
                                                    <option value="">Select Type</option>
                                                    {permitTypes.map((t: any) => <option key={t.id} value={t.name}>{t.name}</option>)}
                                                </Input>
                                                <FormFeedback>{errors.permit_type?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Permit Authority</Label>
                                    <Controller
                                        name="permit_authority"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.permit_authority}>
                                                    <option value="">Select Authority</option>
                                                    {permitAuthorities.map((a: any) => <option key={a.id} value={a.name}>{a.name}</option>)}
                                                </Input>
                                                <FormFeedback>{errors.permit_authority?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Permit Number</Label>
                                    <Controller
                                        name="permit_number"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Permit No" invalid={!!errors.permit_number} autoComplete="off" />
                                                <FormFeedback>{errors.permit_number?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Issue Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="issue_date"
                                        control={control}
                                        rules={{ required: "Issue Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.issue_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedExpiryDate && d && new Date(d) > new Date(watchedExpiryDate)) {
                                                            toast.error("Issue date cannot be after Expiry date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className="d-block">{errors.issue_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Expiry Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="expiry_date"
                                        control={control}
                                        rules={{ required: "Expiry Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.expiry_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedIssueDate && d && new Date(d) < new Date(watchedIssueDate)) {
                                                            toast.error("Expiry date cannot be before Issue date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className="d-block">{errors.expiry_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Renewal Amount ({config.currency}) <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="amount"
                                        control={control}
                                        rules={{ required: "Amount is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="number" {...field} placeholder={`Amount (${config.currency})`} invalid={!!errors.amount} autoComplete="off" />
                                                <FormFeedback>{errors.amount?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Permit Document <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="document"
                                        control={control}
                                        rules={{ required: !docName && "Document is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    accept="image/*,.pdf"
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                            const file = e.target.files[0];
                                                            if (file.size > 2 * 1024 * 1024) {
                                                                toast.error("File size must be less than 2MB");
                                                                e.target.value = "";
                                                                onChange(null);
                                                                return;
                                                            }
                                                        }
                                                        onChange(e.target.files);
                                                    }}
                                                    invalid={!!errors.document}
                                                    autoComplete="off"
                                                />
                                                <FormFeedback>{errors.document?.message}</FormFeedback>
                                                {docName && !(value instanceof FileList && value.length > 0) && (
                                                    <a href={`${mediaUrl}${docName}`} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                )}
                                                {value instanceof FileList && value.length > 0 && (
                                                    <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={12}>
                                    <Label>Remarks</Label>
                                    <Controller
                                        name="remarks"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input type="textarea" {...field} placeholder="Any remarks..." />
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            {/* Pending warning */}
                            {submissionBlocked && (
                                <div className="alert alert-warning mt-3 py-2 text-danger">
                                    <strong>⚠ Blocked:</strong> {pendingMessage}
                                </div>
                            )}

                            {hasPermission('permit-renewal.edit') && (
                                <div className="mt-4">
                                    <Btn color="primary" disabled={loading} type="submit">
                                        {loading ? <>Submitting <Spinner size="sm" /></> : "Submit"}
                                    </Btn>
                                </div>
                            )}
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
